import { buildURL } from './config';

// Auth context for storing authentication information between requests
// This allows us to access auth headers without passing them explicitly each time
// It should be set before making any API requests
let authContext: any = null;

export const setAuthContext = (auth: any) => {
  authContext = auth;
};

interface RestClientOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
}

/**
 * Execute a REST API request with retry logic
 */
export const restRequest = async <T = any>(
  endpoint: string,
  options: RequestInit & RestClientOptions = {}
): Promise<T> => {
  const {
    maxRetries = 2,
    baseDelay = 2000,
    maxDelay = 8000,
    ...fetchOptions
  } = options;

  const url = buildURL(endpoint);
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Get auth headers from auth context
      let authHeaders = {};
      if (authContext?.getAuthHeaders) {
        // Check if getAuthHeaders is async
        const headers = authContext.getAuthHeaders();
        authHeaders = headers instanceof Promise ? await headers : headers;
      }
      
      const response = await fetch(url, {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // Add CORS headers
          'Origin': window.location.origin,
          // Add auth headers
          ...authHeaders,
          ...fetchOptions.headers,
        },
        credentials: 'include', // Include cookies for CORS requests
      });

      // Handle network errors
      if (!response.ok) {
        const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        
        if (response.status === 429) {
          error.isRateLimit = true;
        } else if (response.status >= 500) {
          error.isServerError = true;
        } else if (response.status === 408 || response.status === 504) {
          error.isTimeout = true;
        } else if (response.status === 401 || response.status === 403) {
          error.isAuthError = true;
        }
        
        // Try to get error message from response body
        try {
          const errorBody = await response.json();
          if (errorBody.message) {
            error.message = errorBody.message;
          }
          error.responseBody = errorBody;
        } catch {
          // Ignore JSON parsing errors
        }
        
        throw error;
      }

      // Parse JSON response
      const result = await response.json();
      return result;
      
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on auth errors or client errors
      if (error.isAuthError || (error.status >= 400 && error.status < 500 && !error.isRateLimit)) {
        throw error;
      }
      
      // Check if it's a network error
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        error.isNetworkError = true;
      }
      
      // If we have retries left and it's a retryable error, wait and retry
      if (attempt < maxRetries && (error.isServerError || error.isTimeout || error.isNetworkError || error.isRateLimit)) {
        const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      throw error;
    }
  }
  
  throw lastError;
};

// Convenience methods for common HTTP methods
export const get = <T = any>(endpoint: string, options?: RestClientOptions): Promise<T> =>
  restRequest<T>(endpoint, { ...options, method: 'GET' });

export const post = <T = any>(endpoint: string, data?: any, options?: RestClientOptions): Promise<T> =>
  restRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });

export const put = <T = any>(endpoint: string, data?: any, options?: RestClientOptions): Promise<T> =>
  restRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });

export const del = <T = any>(endpoint: string, options?: RestClientOptions): Promise<T> =>
  restRequest<T>(endpoint, { ...options, method: 'DELETE' });

export default {
  restRequest,
  get,
  post,
  put,
  del,
  setAuthContext,
};